import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { aaiApi } from '../../services/aaiApi';
import type { PersonalSkill, SkillPrefill } from '../../types/aai';
import {
  Action, Card, Chip, Drawer, ErrorNote, Field, Input, Kicker, Loading, StepTrack,
} from '../../components/aa';
import type { TrackStep } from '../../components/aa';

/*
 * The skill builder, on this portal's own kit.
 *
 * It used to be the older builder mounted inside a panel, which meant a person
 * crossed a visual boundary in the middle of a task: a different palette, a
 * different button, a second page heading under the panel's own. The steps and
 * the endpoints are the same ones; only the surface changed.
 *
 * Two kinds of skill, one shape. A context skill is a block about you that you
 * paste before an AI task. A workflow skill is a job task written down so it
 * runs the same way every time, and it is the kind that can later be offered
 * to your team.
 */

export type SkillMode = 'context' | 'workflow';

const STEPS: Record<SkillMode, TrackStep[]> = {
  workflow: [
    { key: 'skill', label: 'The skill' },
    { key: 'steps', label: 'The steps' },
    { key: 'example', label: 'An example' },
    { key: 'review', label: 'Review' },
  ],
  context: [
    { key: 'skill', label: 'The skill' },
    { key: 'steps', label: 'How to use it' },
    { key: 'example', label: 'About you' },
    { key: 'review', label: 'Review' },
  ],
};

const COPY: Record<SkillMode, { title: string; sub: string; nameHint: string; stepsHint: string; exampleLabel: string; exampleHint: string }> = {
  workflow: {
    title: 'Build a workflow skill',
    sub: 'Turn one recurring task into something you run the same way every time.',
    nameHint: 'Name it the way you would say it to a colleague.',
    stepsHint: 'One step per line: when you use it, what context you paste, what you ask for, what you check, and where the result goes.',
    exampleLabel: 'Example input',
    exampleHint: 'A real example makes the skill usable by someone else later. You can leave it empty and add it after your first run.',
  },
  context: {
    title: 'Build an about-you skill',
    sub: 'A block of context about you and your work. Paste it before an AI task and the answers fit your role.',
    nameHint: 'What you will look for when you come back to it.',
    stepsHint: 'One step per line: how you use this block before a task.',
    exampleLabel: 'About you',
    exampleHint: 'This is the block you paste. Edit anything that is not right; it was drafted from your onboarding answers.',
  },
};

const TEXTAREA = 'w-full rounded-act border border-input bg-white px-3 py-2 text-[13.5px] leading-[22px] text-ink outline-none transition-colors focus:border-ink focus:ring-[3px] focus:ring-ink/10';

const toList = (raw: string): string[] => raw.split('\n').map((line) => line.trim()).filter(Boolean);

const ReviewLine: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="border-b border-line-soft py-3 last:border-b-0">
    <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">{label}</div>
    <div className="mt-1 whitespace-pre-line text-[13.5px] leading-[22px] text-ink">{value}</div>
  </div>
);

interface Form {
  name: string;
  problem: string;
  steps: string;
  example_input: string;
  example_output: string;
  note: string;
}

const EMPTY: Form = { name: '', problem: '', steps: '', example_input: '', example_output: '', note: '' };

export const SkillBuilderBody: React.FC<{
  mode: SkillMode;
  /** Revising an existing skill of your own rather than writing a new one. */
  skill?: PersonalSkill | null;
  onSaved?: (skill: PersonalSkill) => void;
}> = ({ mode, skill = null, onSaved }) => {
  const copy = COPY[mode];
  const steps = STEPS[mode];
  const [stage, setStage] = useState(0);
  const [form, setForm] = useState<Form>(EMPTY);
  const [prefill, setPrefill] = useState<SkillPrefill | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<PersonalSkill | null>(null);

  const load = useCallback(async () => {
    if (skill) {
      setForm({
        name: skill.name,
        problem: skill.problem || '',
        steps: (skill.process_steps || []).join('\n'),
        example_input: skill.example_input || '',
        example_output: skill.example_output || '',
        note: '',
      });
      setLoading(false);
      return;
    }
    try {
      const res = await aaiApi.getSkillPrefill(mode);
      setPrefill(res.data);
      setForm({
        name: res.data.name || '',
        problem: res.data.problem || '',
        steps: (res.data.process_steps || []).join('\n'),
        example_input: res.data.example_input || '',
        example_output: res.data.example_output || '',
        note: '',
      });
    } catch {
      setForm(EMPTY);
    } finally {
      setLoading(false);
    }
  }, [mode, skill]);

  useEffect(() => { void load(); }, [load]);

  const set = (key: keyof Form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const body = {
        name: form.name.trim(),
        problem: form.problem.trim(),
        process_steps: toList(form.steps),
        example_input: form.example_input.trim(),
        example_output: form.example_output.trim(),
      };
      let result: PersonalSkill;
      if (skill) {
        result = (await aaiApi.updateMySkill(skill.id, { ...body, note: form.note.trim() || undefined })).data;
        toast.success('Saved. Your earlier version is kept.');
      } else {
        result = (await aaiApi.createMySkill({ ...body, kind: mode })).data;
        toast.success('Saved. It is private to you until you offer it.');
      }
      setSaved(result);
      if (onSaved) onSaved(result);
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail || 'Could not save this skill. Nothing was written, so you can try again.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Loading label="Opening the builder" />;

  if (saved) {
    return (
      <Card pad="md">
        <div className="flex flex-wrap items-center gap-2.5">
          <span className="text-[13px] font-semibold text-ink">{saved.name}</span>
          <Chip tone="ok" dot>Saved</Chip>
        </div>
        <p className="m-0 mt-2 text-[12.5px] leading-[20px] text-ink-dim">
          {mode === 'workflow'
            ? 'Use it on real work and record how each run went. Once it has worked three times, offering it to your team is yours to offer.'
            : 'Paste this block before an AI task so the answers fit your role. Edit it whenever your work changes.'}
        </p>
      </Card>
    );
  }

  const missing: string[] = [];
  if (!form.name.trim()) missing.push('a name');
  if (toList(form.steps).length === 0) missing.push('at least one step');

  const stageBody: Record<number, React.ReactNode> = {
    0: (
      <div className="space-y-5">
        <Field label="Name" htmlFor="skill-name" hint={copy.nameHint}>
          <Input id="skill-name" value={form.name} onChange={set('name')} placeholder="My weekly summary workflow" />
        </Field>
        <Field label="What it is for" htmlFor="skill-problem" hint="One or two sentences. This is what you will read when you come back to it.">
          <textarea id="skill-problem" className={TEXTAREA} rows={3} value={form.problem} onChange={set('problem')} />
        </Field>
        {prefill && !prefill.has_brief && (
          <p className="m-0 text-[12.5px] leading-[20px] text-ink-dim">
            Your onboarding answers would have drafted this for you. You can still write it yourself.
          </p>
        )}
        {prefill?.act_policy === 'no' && (
          <p className="m-0 text-[12.5px] leading-[20px] text-ink-dim">
            Your delegation profile says nothing is sent without you, so the last step reflects that.
          </p>
        )}
        {prefill?.act_policy === 'approval' && (
          <p className="m-0 text-[12.5px] leading-[20px] text-ink-dim">
            Your delegation profile says nothing is sent without your approval, so the last step reflects that.
          </p>
        )}
      </div>
    ),
    1: (
      <Field label="The steps" htmlFor="skill-steps" hint={copy.stepsHint}>
        <textarea id="skill-steps" className={TEXTAREA} rows={9} value={form.steps} onChange={set('steps')} />
      </Field>
    ),
    2: (
      <div className="space-y-5">
        <Field label={copy.exampleLabel} htmlFor="skill-input" hint={copy.exampleHint}>
          <textarea id="skill-input" className={TEXTAREA} rows={mode === 'context' ? 10 : 5} value={form.example_input} onChange={set('example_input')} />
        </Field>
        {mode === 'workflow' && (
          <Field label="A good result" htmlFor="skill-output" hint="What the output looks like when it is right. Optional.">
            <textarea id="skill-output" className={TEXTAREA} rows={5} value={form.example_output} onChange={set('example_output')} />
          </Field>
        )}
      </div>
    ),
    3: (
      <div>
        <div className="rounded-card border border-line px-5 py-2">
          <ReviewLine label="Name" value={form.name.trim() || 'Not written yet'} />
          <ReviewLine label="What it is for" value={form.problem.trim() || 'Not written'} />
          <ReviewLine
            label="Steps"
            value={toList(form.steps).length === 0 ? 'None yet' : toList(form.steps).map((s, i) => `${i + 1}. ${s}`).join('\n')}
          />
          <ReviewLine label={copy.exampleLabel} value={form.example_input.trim() || 'Not written'} />
          {mode === 'workflow' && <ReviewLine label="A good result" value={form.example_output.trim() || 'Not written'} />}
        </div>
        {skill && (
          <div className="mt-5">
            <Field label="What you changed and why" htmlFor="skill-note" hint="Kept with the version, so a later reader knows what moved.">
              <Input id="skill-note" value={form.note} onChange={set('note')} placeholder="Added the trading calendar as an input" />
            </Field>
          </div>
        )}
        <p className="m-0 mt-4 text-[12.5px] leading-[20px] text-ink-dim">
          This stays private to you. Nobody else sees it unless you offer it to your team, and offering it is a
          separate step you take from your own skills list.
        </p>
      </div>
    ),
  };

  const last = stage === steps.length - 1;

  return (
    <div>
      <StepTrack steps={steps} current={stage} className="mb-6" />
      {error && <div className="mb-4"><ErrorNote message={error} /></div>}
      {stageBody[stage]}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        {stage > 0 && <Action variant="quiet" onClick={() => setStage(stage - 1)}>Back</Action>}
        {!last && <Action arrow onClick={() => setStage(stage + 1)}>Next</Action>}
        {last && (
          <Action
            onClick={save}
            disabled={busy || missing.length > 0}
            reason={missing.length > 0 ? `Still needs ${missing.join(' and ')}` : 'Saving'}
          >
            {skill ? 'Save this version' : 'Save the skill'}
          </Action>
        )}
      </div>
    </div>
  );
};

/** The builder in the standard panel. */
export const SkillBuilderDrawer: React.FC<{
  mode: SkillMode;
  skill?: PersonalSkill | null;
  onClose: () => void;
  onSaved?: (skill: PersonalSkill) => void;
}> = ({ mode, skill = null, onClose, onSaved }) => (
  <Drawer
    onClose={onClose}
    kicker={<Kicker>Skill builder</Kicker>}
    badge={skill ? <Chip>Revising</Chip> : undefined}
    title={skill ? skill.name : COPY[mode].title}
    sub={COPY[mode].sub}
    footer={<div className="flex justify-end"><Action size="lg" variant="quiet" onClick={onClose}>Close</Action></div>}
  >
    <SkillBuilderBody mode={mode} skill={skill} onSaved={onSaved} />
  </Drawer>
);

/** The builder as a feature panel: the route decides which kind is being built. */
const SkillBuilderPanel: React.FC<{ panelRoute?: string }> = ({ panelRoute }) => {
  const mode: SkillMode = (panelRoute || '').includes('/workflow') ? 'workflow' : 'context';
  return <SkillBuilderBody mode={mode} />;
};

export default SkillBuilderPanel;
